import { Banknote, FileText } from 'lucide-react';

import stripeLogo from '../../assets/payment-logos/stripe.svg';
import venmoLogo from '../../assets/payment-logos/venmo.png';
import cashappLogo from '../../assets/payment-logos/cashapp.svg';
import paypalLogo from '../../assets/payment-logos/paypal.png';
import zelleLogo from '../../assets/payment-logos/zelle.svg';

export type PaymentMethodKey = 'stripe' | 'venmo' | 'cashapp' | 'paypal' | 'zelle' | 'check' | 'cash';

interface PaymentMethodLogoProps {
  method: PaymentMethodKey;
  size?: number;
}

const BRAND_LOGOS: Partial<Record<PaymentMethodKey, { src: string; alt: string }>> = {
  stripe: { src: stripeLogo, alt: 'Stripe' },
  venmo: { src: venmoLogo, alt: 'Venmo' },
  cashapp: { src: cashappLogo, alt: 'Cash App' },
  paypal: { src: paypalLogo, alt: 'PayPal' },
  zelle: { src: zelleLogo, alt: 'Zelle' },
};

/**
 * Renders a branded icon for each payment method.
 *
 * Brand logos (Stripe, Venmo, Cash App, PayPal, Zelle) render at a fixed
 * height with auto width so they aren't constrained to a square.
 * Check and Cash use Lucide icons (no brand logos needed).
 */
export function PaymentMethodLogo({ method, size = 24 }: PaymentMethodLogoProps) {
  const logo = BRAND_LOGOS[method];
  if (logo) {
    return (
      <img
        src={logo.src}
        alt={logo.alt}
        style={{ height: size, width: 'auto' }}
        className="object-contain shrink-0"
      />
    );
  }

  switch (method) {
    case 'check':
      return (
        <div className="flex items-center justify-center" style={{ width: size, height: size }}>
          <FileText size={size * 0.8} className="text-gray-500" />
        </div>
      );
    case 'cash':
      return (
        <div className="flex items-center justify-center" style={{ width: size, height: size }}>
          <Banknote size={size * 0.8} className="text-green-600" />
        </div>
      );
    default:
      return null;
  }
}

/** Human-readable label for each payment method. */
export const PAYMENT_METHOD_LABELS: Record<PaymentMethodKey, string> = {
  stripe: 'Credit / Debit',
  venmo: 'Venmo',
  cashapp: 'Cash App',
  paypal: 'PayPal',
  zelle: 'Zelle',
  check: 'Check',
  cash: 'Cash',
};

/** All available payment method keys in display order. */
export const ALL_PAYMENT_METHODS: PaymentMethodKey[] = [
  'stripe', 'venmo', 'cashapp', 'paypal', 'zelle', 'check', 'cash',
];
